'use client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useState } from 'react';
import { AgentLineAgent2 } from '@/app/components/icons/AgentLineAgent2';
import { AgentLineDialogue } from '@/app/components/icons/AgentLineDialogue';
import { BaseLineApi } from '@/app/components/icons/BaseLineApi';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineLanguage } from '@/app/components/icons/BaseLineLanguage';
import { BaseLineMessage } from '@/app/components/icons/BaseLineMessage';
import { BaseLineProfile } from '@/app/components/icons/BaseLineProfile';
import { BaseLineQuickEdit } from '@/app/components/icons/BaseLineQuickEdit';
import { LoginWhite } from '@/app/components/icons/LoginWhite';
import { ProductLineCreations } from '@/app/components/icons/ProductLineCreations';
import { ProductLineHome } from '@/app/components/icons/ProductLineHome';
import { ProductLineReference } from '@/app/components/icons/ProductLineReference';
import { ProductLineTemplates } from '@/app/components/icons/ProductLineTemplates';
import { ProductLineText2Image } from '@/app/components/icons/ProductLineText2Image';
import { MobileNotifications } from '@/app/components/shared/NotificationCenter';
import { useNotifications } from '@/app/components/shared/NotificationCenter/hook/use-notifications';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { API_URL, GLOBAL_LANGUAGES_CONFIG, HOME_PAGE, isEnterpriseDistribution, SITE } from '@/app/constants';
import { PAGE_CONFIG_KEY, useDevice, usePageConfigs } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SupportedLanguage } from '@/app/types';
import { isDistributionRouteVisible, reportEvent, setRegisterPage } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { MenuItem } from '../../Sidebar/components/MenuItem';
import type { MenuItemType } from '../../Sidebar/types';

const MENU_ITEM_ICON_CLASSNAME = 'size-5 text-system-white';

const MENU_DIVIDER_AFTER_IDS = new Set([
  's1',
  'generate-image',
  'tools',
  'home',
  'agent',
  'assets',
  'templates',
  'system',
]);

export const MobileMenu: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const trans = useTranslations();
  const { isViduApp } = useDevice();
  const { unreadCount, activeTab } = useNotifications();

  const { getPageConfigs, configData } = usePageConfigs([PAGE_CONFIG_KEY.templateEntranceLabel]);
  const { isTeamSpace } = useStore((state) => ({
    isTeamSpace: state.isTeamSpace,
  }));
  useEffect(() => {
    getPageConfigs();
  }, []);

  // Temporarily hide the workbench earn entrance exposure reporting.
  // To restore, uncomment this block and reintroduce `useRef` plus `userInfo` store selection above.
  // const hasReportedEarnEntranceExposeRef = useRef(false);
  // const userInfo = useStore((state) => state.userInfo);
  //
  // useEffect(() => {
  //   if (!userInfo || hasReportedEarnEntranceExposeRef.current) return;
  //
  //   reportEvent('earn_home_entrance_expose', { from_source: 'workbench' });
  //   hasReportedEarnEntranceExposeRef.current = true;
  // }, [userInfo]);

  const handleSelectLanguage = (language: SupportedLanguage) => {
    setSelectedLanguage(language);
  };

  const handleLanguageChange = () => {
    if (selectedLanguage) {
      router.replace(pathname, { locale: selectedLanguage });
      setRegisterPage({
        language: selectedLanguage,
      });
      setLanguageDialogOpen(false);
    }
  };

  // 初始化选择当前语言
  useEffect(() => {
    if (languageDialogOpen) {
      setSelectedLanguage(locale);
    }
  }, [languageDialogOpen, locale]);

  const menuItems: MenuItemType[] = [
    {
      name: trans('sidebar.home'),
      icon: <ProductLineHome className={MENU_ITEM_ICON_CLASSNAME} />,
      path: HOME_PAGE,
      id: 'home',
    },
    ...(isTeamSpace
      ? [
          {
            name: trans('sidebar.teamProfile'),
            icon: <BaseLineProfile className={MENU_ITEM_ICON_CLASSNAME} />,
            path: '/team-profile',
            id: 'team-profile',
          } satisfies MenuItemType,
        ]
      : []),
    {
      name: trans('sidebar.s1'),
      icon: <AgentLineDialogue className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/s1',
      id: 's1',
      onClick: () => reportEvent('element_click', { element_name: 'menu - s1' }),
    },
    {
      name: trans('sidebar.toolsItems.referenceToVideo'),
      icon: <ProductLineReference className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/character2video',
      activePaths: ['/create/img2video', '/create/text2video'],
      id: 'video-generation',
    },
    {
      name: trans('sidebar.generateImage'),
      icon: <ProductLineText2Image className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/reference2image',
      activePaths: ['/create/text2image'],
      id: 'generate-image',
      onClick: () => {
        reportEvent('element_click', {
          element_name: 'menu - generate-image',
        });
      },
    },
    {
      name: trans('sidebar.labItems.agent'),
      icon: <AgentLineAgent2 className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/project',
      activePaths: ['/claw'],
      id: 'agent',
      onClick: () => {
        reportEvent('element_click', { element_name: 'menu - agent' });
        reportEvent('project_page_view', { page_type: 'project_list_page', enter_from: 'home_page' });
      },
    },
    {
      name: trans('sidebar.assets'),
      icon: <ProductLineCreations className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/assets/mycreations',
      activePaths: ['/assets/myreferences', '/assets/liverecords'],
      id: 'assets',
    },
    {
      name: trans('sidebar.labItems.templates'),
      icon: <ProductLineTemplates className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/templates',
      id: 'templates',
      tag: configData?.[PAGE_CONFIG_KEY.templateEntranceLabel],
      onClick: () => {
        reportEvent('templates_exposure', {
          refer_element: 'menu - templates',
        });
      },
    },
    {
      name: trans('sidebar.tools'),
      icon: <BaseLineQuickEdit className={MENU_ITEM_ICON_CLASSNAME} />,
      path: '/create/tools',
      id: 'tools',
    },

    ...(isEnterpriseDistribution
      ? []
      : [
          {
            name: trans('sidebar.service'),
            type: 'group',
            children: [
              {
                name: trans('sidebar.serviceItems.api'),
                icon: <BaseLineApi className="text-white text-xl opacity-70" />,
                onClick: () => {
                  reportEvent('element_click', {
                    element_name: 'API',
                  });
                  openOrRedirect(API_URL);
                },
              },
            ],
          } as MenuItemType,
          {
            name: trans('sidebar.system'),
            type: 'group',
            id: 'system',
            children: [
              // Temporarily hide the Vidu Earn mobile menu entry.
              // {
              //   name: trans('header.tabs.viduEarn.label'),
              //   icon: <BaseLineApi className="text-white text-xl opacity-70" />,
              //   path: '/earn',
              //   onClick: () => {
              //     reportEvent('earn_home_entrance_click', { from_source: 'workbench' });
              //   },
              // } satisfies MenuItemType,
              ...(SITE === 'global'
                ? [
                    {
                      name: trans('sidebar.systemItems.language'),
                      icon: <BaseLineLanguage className={MENU_ITEM_ICON_CLASSNAME} />,
                      onClick: () => {
                        setLanguageDialogOpen(true);
                      },
                    },
                  ]
                : []),
              {
                name: trans('sidebar.systemItems.notification'),
                icon: (
                  <div className="relative">
                    <BaseLineMessage className="text-white text-xl opacity-70" />
                    {unreadCount > 0 && (
                      <div className="-right-1 -top-1 absolute flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-system-error1 px-1 text-[11px] text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </div>
                    )}
                  </div>
                ),
                onClick: () => {
                  setNotificationOpen(true);
                  reportEvent('notification_button_click', {
                    open: true,
                    type: activeTab === 'system' ? 'platform_messages' : 'interactive_massages',
                  });
                },
              },
            ],
          } as MenuItemType,
        ]),
  ];
  const visibleMenuItems: MenuItemType[] = menuItems
    .map((item) => {
      if (item.type !== 'group') {
        return item;
      }

      return {
        ...item,
        children: item.children.filter((child) => isDistributionRouteVisible(child.path)),
      };
    })
    .filter((item) => (item.type === 'group' ? item.children.length > 0 : isDistributionRouteVisible(item.path)));

  return (
    <>
      <div className="flex h-screen w-full flex-col justify-start overflow-y-auto bg-system-bg08 px-4 text-[14px] text-white sm:pb-[var(--ios-safe-bottom)]">
        <div className="flex h-20 shrink-0 items-center justify-between md:h-16">
          {!isEnterpriseDistribution ? <LoginWhite className="text-3xl text-system-white" /> : <div />}
          <div className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-system-white12">
            <BaseLineClose className="size-5" onClick={onClose} />
          </div>
        </div>
        <nav>
          {visibleMenuItems.map((item) => {
            const shouldShowDivider = item.id ? MENU_DIVIDER_AFTER_IDS.has(item.id) : false;

            return (
              <div key={item.name}>
                {item.type !== 'group' ? (
                  <div className="mb-1">
                    <MenuItem
                      name={item.name}
                      icon={item.icon}
                      path={item.path || HOME_PAGE}
                      activePaths={item.activePaths}
                      tag={typeof item.tag === 'string' ? item.tag : undefined}
                      onClick={() => {
                        item.onClick?.();
                        item.path && onClose?.();
                      }}
                      id={item.id}
                      exact={item.exact}
                    />
                  </div>
                ) : (
                  <div className="cursor-pointer">
                    <div className="mt-6 mb-3 flex items-center gap-2 px-4">
                      <span className="text-system-white48">{item.name}</span>
                      {item.tag}
                    </div>
                    {item.children?.map((child) => (
                      <MenuItem
                        key={child.name}
                        className="mb-1"
                        {...child}
                        onClick={() => {
                          child.onClick?.();
                          // 跳转页面关闭菜单
                          child.path && onClose?.();
                        }}
                      />
                    ))}
                  </div>
                )}
                {shouldShowDivider && (
                  <div className="my-2 px-4">
                    <div className="h-px w-full bg-system-white08" />
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="mt-6 flex flex-col gap-3 border-system-white12 border-t-[1px] p-4 pt-6 text-system-white64">
          {isViduApp && (
            <nav className="flex items-center gap-3">
              <div
                onClick={() => {
                  router.push('/terms');
                }}
              >
                <span className="">{trans('sidebar.terms')}</span>
              </div>
              <div
                onClick={() => {
                  router.push('/privacy');
                }}
              >
                <span className="">{trans('sidebar.privacy')}</span>
              </div>
            </nav>
          )}
          {!isEnterpriseDistribution && <div className="flex items-center">{trans('sidebar.serviceEmail')}</div>}
        </div>
      </div>
      {/* 通知弹窗 */}
      <MobileNotifications open={notificationOpen} onOpenChange={setNotificationOpen} />

      {/* 语言选择弹窗 */}
      <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
        <DialogContent
          className="w-[296px] rounded-xl border border-white/10 bg-system-bg04 p-0 shadow-[0px_20px_60px_0px_rgba(0,0,0,0.32)] md:p-0"
          closable={false}
        >
          <div className="flex flex-col gap-4 px-6 pt-4 pb-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center">
                <div className="font-medium text-system-text01 text-xl leading-[140%]">
                  {trans('sidebar.systemItems.language')}
                </div>
              </div>
              <div className="flex flex-col">
                {GLOBAL_LANGUAGES_CONFIG.map(({ label, value }) => (
                  <div
                    key={value}
                    className={`flex items-center justify-between rounded-10 p-4 ${selectedLanguage === value ? 'bg-white/10' : ''}`}
                    onClick={() => handleSelectLanguage(value)}
                  >
                    <div className="flex-1 font-normal text-base text-white leading-5">{label}</div>
                    <div
                      className={`relative h-5 w-5 overflow-hidden ${selectedLanguage !== value ? 'opacity-0' : ''}`}
                    >
                      <BaseLineCheck2 className="absolute text-white" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                className="flex h-9 flex-1 items-center justify-center rounded-lg border border-white/10 px-6"
                onClick={() => setLanguageDialogOpen(false)}
              >
                <span className="translate-y-[-0.5px] font-normal text-sm text-white leading-7">
                  {trans('sidebar.systemItems.cancel')}
                </span>
              </button>
              <button
                type="button"
                className="flex h-9 flex-1 items-center justify-center rounded-lg bg-ShengshuButton px-6"
                onClick={handleLanguageChange}
              >
                <span className="translate-y-[-0.5px] font-normal text-black text-sm leading-7">
                  {trans('sidebar.systemItems.confirm')}
                </span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
